/**
 * export_audit_bundle — safe agent tool. Triggers the audit export
 * endpoint and returns the signed URL.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

type AuditFormat = 'pdf' | 'rdm-csv-zip' | 'oscal-assessment-results';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

interface ExportResponse {
  exportId: string;
  format: AuditFormat;
  signedUrl: string;
  expiresAt: string;
  byteSize: number;
  graphVersionHash: string;
}

export const exportAuditBundleTool: IAgentTool = {
  name: 'export_audit_bundle',
  description:
    'Render and store an audit bundle for the current folder + framework, then return a 1-hour signed download URL. Format options: pdf (single audit PDF), rdm-csv-zip (raw bundle), oscal-assessment-results (OSCAL JSON). Use when the user asks to export, download, or generate the audit deliverable.',
  parameters: {
    type: 'object',
    properties: {
      framework: { type: 'string', description: 'Framework id (default soc2).' },
      format: {
        type: 'string',
        description: 'Export format. Default: pdf.',
        enum: ['pdf', 'rdm-csv-zip', 'oscal-assessment-results'],
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const framework = typeof params.framework === 'string' && params.framework ? params.framework : 'soc2';
    const format =
      typeof params.format === 'string' && params.format
        ? (params.format as AuditFormat)
        : ('pdf' as AuditFormat);

    const url =
      `/api/compass/folders/${encodeURIComponent(folderId)}` +
      `/compliance/${encodeURIComponent(framework)}/export?format=${encodeURIComponent(format)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      return { success: false, summary: `Export failed (${response.status})${text ? `: ${text.slice(0, 200)}` : ''}` };
    }
    const body = (await response.json()) as ExportResponse;
    const sizeKb = Math.round(body.byteSize / 1024);
    return {
      success: true,
      summary:
        `${framework} ${format} export ready (${sizeKb} KiB). ` +
        `Signed URL valid until ${body.expiresAt}.\n\n${body.signedUrl}`,
      data: {
        exportId: body.exportId,
        format: body.format,
        signedUrl: body.signedUrl,
        expiresAt: body.expiresAt,
        byteSize: body.byteSize,
        graphVersionHash: body.graphVersionHash,
      },
      createdResourceIds: [body.exportId],
      uiSurface: 'chat',
    };
  },
};
